import React from 'react';
import { colors } from '../../utils';
import { useTranslation } from 'react-i18next';
import { Icon } from '@mui/material';
import { tokens } from '../../theme/tokens';
import {
  Bar,
  BarChart,
  LineChart,
  Line,
  CartesianGrid,
  Label,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { Title, LegacyGrid as Grid } from '../../elements';
import dayjs from 'dayjs';
import { AnimalWeight, AnimalWeightMeasurement } from '../../models/animal-view';
import { DISPLAY_WEIGHT_UNIT, convertWeight, getWeightUnitLabel } from '../../utils';

interface IProps {
  weight: AnimalWeight;
  showTitle?: boolean;
}

const AnimalViewWeightsGraph = ({ weight, showTitle = false }: IProps) => {
  const { t } = useTranslation();

  // Always kilograms - the farm's weight type is not consulted, and a single Y axis cannot mix
  // grams for the birth bar with kilograms for the slaughter bar.
  const weightUnit = DISPLAY_WEIGHT_UNIT;

  const [shouldCallSearch, setShouldCallSearch] = React.useState(true);

  const convertWeightValue = (weightData: AnimalWeightMeasurement | null | undefined): number | null => {
    if (!weightData) return null;
    return convertWeight(weightData.value, weightData.unit, weightUnit);
  };

  const renderWeightsGraph = (weight: AnimalWeight) => {
    const data = [
      { name: t('f-animal-birth'), weight: convertWeightValue(weight?.birthed) },
      { name: t('f-animal-weaned'), weight: convertWeightValue(weight?.weaned) },
      { name: t('f-animal-ten-weeks'), weight: convertWeightValue(weight?.tenWeeks) },
      { name: t('f-animal-slaughtered'), weight: convertWeightValue(weight?.slaughtered) }
    ];

    const unitLabel = getWeightUnitLabel(weightUnit, t);

    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} style={CHART_FONT}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name">
            <Label offset={0} position="bottom" />
          </XAxis>
          <YAxis label={{ value: unitLabel, angle: -90, position: 'insideLeft' }} />
          <Tooltip formatter={(value: any) => `${value} ${unitLabel}`} />
          <Bar dataKey="weight" barSize={25} fill={colors.blue}>
            {/*<LabelList dataKey="name" position="top" />*/}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const renderWeightsInTime = (birthedDate: string, weights: AnimalWeightMeasurement[]) => {
    // Map of day (since birth) -> { totalWeight: number, count: number }
    const dataMap: Record<number, { totalWeight: number; count: number }> = {};

    weights.forEach((entry) => {
      const daysSinceBirth = dayjs(entry.date).diff(dayjs(birthedDate), 'days');

      if (!dataMap[daysSinceBirth]) {
        dataMap[daysSinceBirth] = { totalWeight: 0, count: 0 };
      }

      // Convert weight value to grams first
      let valueInGrams = entry.value;
      if (entry.unit === 2) {
        valueInGrams = entry.value * 1000;
      }

      dataMap[daysSinceBirth].totalWeight += valueInGrams;
      dataMap[daysSinceBirth].count += 1;
    });

    const chartData = Object.keys(dataMap)
      .map((day) => {
        const dayNum = parseInt(day, 10);
        const { totalWeight, count } = dataMap[dayNum];
        const avgWeightInGrams = totalWeight / count;
        return {
          name: dayNum,
          weight: convertWeight(avgWeightInGrams, 1, weightUnit)
        };
      })
      .sort((a, b) => a.name - b.name);

    const unitLabel = getWeightUnitLabel(weightUnit, t);

    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} style={CHART_FONT}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" label={{ value: t('f-days'), position: 'insideBottom' }} />
          <YAxis label={{ value: unitLabel, angle: -90, position: 'insideLeft' }} />
          <Tooltip formatter={(value: any) => `${value} ${unitLabel}`} />
          <Line type="monotone" dataKey="weight" stroke={colors.blue} strokeWidth={2} />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  React.useEffect(() => {
    if (shouldCallSearch === true) {
      setShouldCallSearch(false);
    }
  }, [shouldCallSearch]);

  return (
    <>
      <Grid item container justifyContent="space-between" alignItems="center" spacing={2}>
        <Grid item>
          {showTitle && (
            <Grid container direction="row" spacing={1} alignItems="center">
              <Grid item>
                <Icon color="primary">scale</Icon>
              </Grid>
              <Grid item>
                <Title>{t('f-animal-weight-title')}</Title>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>

      <Grid container>
        <Grid item xs={12}>
          <div className="h-[200px] w-full">{renderWeightsGraph(weight)}</div>
        </Grid>
      </Grid>

      {weight?.weights && weight.weights.length > 0 && (
        <Grid container>
          <Grid item xs={12}>
            <div className="h-[200px] w-full">{renderWeightsInTime(weight?.birthed?.date, weight.weights)}</div>
          </Grid>
        </Grid>
      )}
      {weight?.weightPerDay ? (
        <Grid container>
          <Grid item>
            <Grid container direction="row" spacing={1} alignItems="center">
              <Grid item>
                <Title>
                  {t('f-animal-daily-grow')}:{' '}
                  {(convertWeight(weight?.weightPerDay || 0, 1, weightUnit) || 0).toFixed(3)} (
                  {getWeightUnitLabel(weightUnit, t)})
                </Title>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      ) : null}
    </>
  );
};

export default AnimalViewWeightsGraph;

const CHART_FONT = { fontSize: '0.9rem', fontFamily: tokens.fontFamily };
